import { useEffect, useMemo, useState, useCallback } from "react";
import { useSnackbar } from "notistack";
import { IconButton, Box, Typography } from "@mui/material";
import DataTable from "../DataTable.jsx";
import { useUser } from "../../hooks/user.js";
import { getUserRowsPerPage } from "../../utils/userSettings.js";
import { useMultiTableData } from "./hooks/useMultiTableData.jsx";
import { applyFilterWithAccessorKeys } from "./utils/tableUtils.js";
import { useIncomingMailActions } from "../../hooks/useIncomingMailActions.jsx";
import { backendGet } from "../../services/dataService.js";
import PropTypes from "prop-types";
import {
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreIcon,
} from "@mui/icons-material";

export default function IncomingMailsTable({
  folder = "not_assigned",
  onRowClick = () => {},
  onFolderChange = () => {},
  selectedMailId = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useUser();
  const [folderCounts, setFolderCounts] = useState(null);

  const fetchFolderCounts = useCallback(async () => {
    const params = {
      api_version: "v2",
    };

    const response = await backendGet("/data/incoming-mails/folder-counts", {
      label: "E-Mail Übersicht",
      enqueueSnackbar,
      options: {
        params,
      },
    });

    if (response?.data) {
      setFolderCounts(response.data);
    }
  }, [enqueueSnackbar]);

  const {
    state: mailsState,
    setState: setMailsState,
    trashMail,
    restoreMail,
  } = useIncomingMailActions(fetchFolderCounts);

  const tableKey = "incoming-mails";
  const storageKey = "v2_table_incoming_mails";
  const defaultRowsPerPage = getUserRowsPerPage(user, 50);

  const tablesConfig = useMemo(() => {
    const filters = {};

    if (folder === "trash") {
      filters.folder = "trash";
    } else if (folder === "assigned") {
      filters.is_assigned = "assigned";
    } else if (folder === "not_assigned") {
      filters.is_assigned = "not_assigned";
    }

    const extraParams = {
      sort: [{ field: "sent_at", order: "desc" }],
      include: "attachments,schaden",
    };

    if (Object.keys(filters).length > 0) {
      extraParams.filters = filters;
    }

    return [
      {
        key: tableKey,
        label: "E-Mails",
        route: `/data/incoming-mails`,
        responseKey: tableKey,
        pageSize: defaultRowsPerPage,
        storageKey: storageKey,
        extraParams,
        onStateChange: setMailsState,
      },
    ];
  }, [defaultRowsPerPage, storageKey, folder]);

  const { tableStates, updateTableState, fetchData } = useMultiTableData(
    tablesConfig,
    enqueueSnackbar
  );

  const tableState = tableStates[tableKey] || {};

  useEffect(() => {
    updateTableState(tableKey, { pageIndex: 0, shouldFetch: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder]);

  const rows = mailsState?.data?.data || [];

  useEffect(() => {
    fetchFolderCounts();
  }, [fetchFolderCounts]);

  const handleTrash = async (mail) => {
    if (mail.deleted_at) {
      await restoreMail(mail.id);
    } else {
      await trashMail(mail.id);
    }
  };

  const filterTabs = useMemo(
    () => [
      {
        key: "all",
        label: "Alle",
        count: folderCounts?.all ?? tableState.totalRows ?? 0,
        onClick: onFolderChange,
      },
      {
        key: "not_assigned",
        label: "Nicht zugeordnet",
        count: folderCounts?.not_assigned ?? 0,
        onClick: onFolderChange,
      },
      {
        key: "assigned",
        label: "Zugeordnet",
        count: folderCounts?.assigned ?? 0,
        onClick: onFolderChange,
      },
      {
        key: "trash",
        label: "Papierkorb",
        count: folderCounts?.trash ?? 0,
        onClick: onFolderChange,
      },
    ],
    [folderCounts, tableState.totalRows, onFolderChange]
  );

  const columns = [
    {
      accessorKey: "subject",
      header: "Betreff",
      size: 400,
      Cell: ({ row }) => (
        <Box>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              overflow: "hidden",
            }}
          >
            {row.original.subject}
          </Typography>
        </Box>
      ),
    },
    {
      accessorKey: "from_email",
      header: "Absender",
      size: 200,
    },
    {
      accessorKey: "sent_at",
      header: "Datum",
      size: 150,
      Cell: ({ cell }) =>
        cell.getValue()
          ? new Date(cell.getValue()).toLocaleDateString("de-DE")
          : "-",
    },
    {
      accessorKey: "schaden",
      header: "Schaden",
      size: 150,
      enableSorting: false,
      Cell: ({ row }) =>
        row.original.schaden_id ? (
          <>{row.original.schaden?.schaden_nummer_makler || "Zugeordnet"}</>
        ) : null,
    },
    {
      id: "actions",
      header: "Aktionen",
      size: 80,
      enableSorting: false,
      enableColumnFilter: false,
      Cell: ({ row }) =>
        !row.original.schaden_id ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleTrash(row.original);
            }}
            title={
              row.original.deleted_at
                ? "E-Mail wiederherstellen"
                : "In Papierkorb verschieben"
            }
          >
            {row.original.deleted_at ? <RestoreIcon /> : <DeleteIcon />}
          </IconButton>
        ) : null,
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={rows}
      initialState={{ showGlobalFilter: true }}
      isLoading={tableState.isLoading}
      hasLoaded={tableState.hasLoaded}
      pageIndex={tableState.pageIndex}
      pageSize={tableState.pageSize}
      totalRows={tableState.totalRows}
      onChangeSorting={(sorting) => updateTableState(tableKey, { sorting })}
      onChangeFilters={(filters) =>
        updateTableState(tableKey, {
          filters: applyFilterWithAccessorKeys(filters, columns),
        })
      }
      onChangeGlobalFilter={(searchword) =>
        updateTableState(tableKey, { searchword })
      }
      onChangePage={(nextOrUpdater) => {
        const base = {
          pageIndex: tableState.pageIndex ?? 0,
          pageSize: tableState.pageSize ?? defaultRowsPerPage,
        };
        const next =
          typeof nextOrUpdater === "function"
            ? nextOrUpdater(base)
            : nextOrUpdater;
        if (!next) {
          return;
        }
        updateTableState(tableKey, {
          pageIndex: next.pageIndex,
          pageSize: next.pageSize,
        });
      }}
      storageKey={storageKey}
      enableHeader
      enableSorting
      enableGlobalFilter={true}
      enableColumnFilters={true}
      enableColumnHiding={true}
      topToolbarIcons={true}
      enableTopToolbar
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => onRowClick(row.original),
        sx: {
          cursor: "pointer",
          backgroundColor:
            row.original.id === selectedMailId ? "#F1F1F199" : "#fff",
        },
      })}
      filterTabs={filterTabs}
      activeFilterTab={folder}
      enableStickyHeader
    />
  );
}

IncomingMailsTable.propTypes = {
  folder: PropTypes.string,
  onRowClick: PropTypes.func,
  onFolderChange: PropTypes.func,
  selectedMailId: PropTypes.number,
};
